
'use client';

/* eslint-disable @next/next/no-img-element */

import { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  Award,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  Clock3,
  ExternalLink,
  Image as ImageIcon,
  Info,
  MapPin,
  MessageCircle,
  Scissors,
  Sparkles,
  Star,
  Store,
  UserRound,
  Zap,
  Facebook,
  Instagram,
  Music2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type {
  ProfileScheduleRange,
  PublicBusinessProfile,
} from '@/lib/dashboard/public-business-profile';

interface BusinessProfileViewProps {
  className?: string;
  profile: PublicBusinessProfile;
  variant?: 'public' | 'preview';
}

function formatCurrency(value: number | null): string {
  if (value === null) return 'A convenir';
  return `$${new Intl.NumberFormat('es-CO').format(value)}`;
}

function formatDuration(value: number | null): string {
  return value ? `${value} min` : 'Duracion por confirmar';
}

function formatTime(value: string): string {
  const [hourText, minute = '00'] = value.split(':');
  const hour = Number(hourText);

  if (!Number.isFinite(hour)) return value;

  const suffix = hour >= 12 ? 'p. m.' : 'a. m.';
  const normalizedHour = hour % 12 || 12;
  return `${normalizedHour}:${minute.padStart(2, '0')} ${suffix}`;
}

function minutesFromTime(time: string): number {
  const [hourText, minuteText = '0'] = time.split(':');
  return Number(hourText) * 60 + Number(minuteText);
}

function formatScheduleRange(ranges: ProfileScheduleRange[]): string {
  const range = ranges[0];
  return range ? `${formatTime(range.start)} - ${formatTime(range.end)}` : 'Cerrado';
}

function getZonedNow(timezone: string): { day: number; minutes: number } | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      hourCycle: 'h23',
      minute: '2-digit',
      timeZone: timezone || 'America/Bogota',
      weekday: 'short',
    }).formatToParts(new Date());
    const weekday = parts.find((part) => part.type === 'weekday')?.value;
    const hour = Number(parts.find((part) => part.type === 'hour')?.value);
    const minute = Number(parts.find((part) => part.type === 'minute')?.value);
    const dayMap: Record<string, number> = {
      Mon: 1,
      Tue: 2,
      Wed: 3,
      Thu: 4,
      Fri: 5,
      Sat: 6,
      Sun: 7,
    };

    if (!weekday || !Number.isFinite(hour) || !Number.isFinite(minute)) return null;
    return { day: dayMap[weekday] ?? 1, minutes: hour * 60 + minute };
  } catch {
    return null;
  }
}

function getOpenStatus(profile: PublicBusinessProfile): { isOpen: boolean; label: string } {
  const now = getZonedNow(profile.timezone);

  if (!now) {
    const firstOpenDay = profile.schedules.find((day) => day.ranges.length > 0);
    return { isOpen: false, label: firstOpenDay ? formatScheduleRange(firstOpenDay.ranges) : 'Horario por confirmar' };
  }

  const today = profile.schedules.find((day) => day.day === now.day);
  const activeRange = today?.ranges.find((range) => {
    const start = minutesFromTime(range.start);
    const end = minutesFromTime(range.end);
    return Number.isFinite(start) && Number.isFinite(end) && now.minutes >= start && now.minutes < end;
  });

  if (activeRange) return { isOpen: true, label: `Abierto hasta ${formatTime(activeRange.end)}` };

  const nextTodayRange = today?.ranges.find((range) => minutesFromTime(range.start) > now.minutes);
  if (nextTodayRange) return { isOpen: false, label: `Abre hoy ${formatTime(nextTodayRange.start)}` };

  const nextOpenDay = profile.schedules.find((day) => day.ranges.length > 0);
  if (!nextOpenDay) return { isOpen: false, label: 'Horario por confirmar' };

  return { isOpen: false, label: `Proximo horario ${nextOpenDay.shortLabel} ${formatScheduleRange(nextOpenDay.ranges)}` };
}

function buildBookingHref(slug: string, params?: { professionalId?: string; serviceId?: string }): string {
  const search = new URLSearchParams();
  if (params?.serviceId) search.set('service', params.serviceId);
  if (params?.professionalId) search.set('professional', params.professionalId);

  const query = search.toString();
  return `/perfiles/${encodeURIComponent(slug)}/reservar/${query ? `?${query}` : ''}`;
}

function normalizeSocialUrl(platform: 'facebook' | 'instagram' | 'tiktok', value: string | null): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  const withoutSlash = trimmed.replace(/^\/+/, '');
  if (/^(www\.)?(facebook|instagram|tiktok)\.com\//i.test(withoutSlash) || /^fb\.com\//i.test(withoutSlash)) {
    return `https://${withoutSlash}`;
  }

  const handle = withoutSlash.replace(/^@/, '');
  if (!handle) return null;

  if (platform === 'facebook') return `https://www.facebook.com/${handle}`;
  if (platform === 'instagram') return `https://www.instagram.com/${handle}`;
  return `https://www.tiktok.com/@${handle}`;
}

function SocialButtons({ profile }: { profile: PublicBusinessProfile }) {
  const links = [
    {
      href: normalizeSocialUrl('facebook', profile.socialLinks.facebook),
      icon: Facebook,
      label: 'Facebook',
    },
    {
      href: normalizeSocialUrl('instagram', profile.socialLinks.instagram),
      icon: Instagram,
      label: 'Instagram',
    },
    {
      href: normalizeSocialUrl('tiktok', profile.socialLinks.tiktok),
      icon: Music2,
      label: 'TikTok',
    },
  ].filter((link): link is { href: string; icon: typeof Facebook; label: string } => Boolean(link.href));

  if (links.length === 0) {
    return null;
  }

  return (
    <div className="absolute right-3 top-3 z-20 flex gap-2">
      {links.map((link) => {
        const Icon = link.icon;
        return (
          <a
            key={link.label}
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={link.label}
            title={link.label}
            className="inline-flex h-10 w-10 items-center justify-center rounded-button border border-white/15 bg-bg-primary/80 text-text-primary shadow-[0_10px_30px_rgba(0,0,0,0.35)] backdrop-blur-md transition-colors hover:border-accent hover:text-accent"
          >
            <Icon size={18} />
          </a>
        );
      })}
    </div>
  );
}

function buildWhatsAppUrl(phone: string | null, businessName: string): string {
  const digits = phone?.replace(/\D/g, '') || '';
  const text = encodeURIComponent(`Hola, quiero reservar una cita en ${businessName}.`);
  return digits ? `https://wa.me/${digits}?text=${text}` : `https://wa.me/?text=${text}`;
}

function getInitials(name: string): string {
  return (
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase())
      .join('') || 'OY'
  );
}

function formatReviewDate(value: string | null): string {
  if (!value) return 'Reciente';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Reciente';

  return new Intl.DateTimeFormat('es-CO', {
    day: 'numeric',
    month: 'short',
  }).format(date);
}

function DirectionsLink({ address }: { address: string | null }) {
  if (!address) return null;

  return (
    <a
      href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&travelmode=driving&dir_action=navigate`}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-sm font-semibold text-accent transition-colors hover:text-accent-hover"
    >
      Como llegar
      <ExternalLink size={14} />
    </a>
  );
}

function ImageFallback({ label, className }: { className?: string; label: string }) {
  return (
    <div className={cn('flex h-full w-full items-center justify-center bg-accent/10 text-accent', className)}>
      <span className="font-heading text-xl font-semibold">{getInitials(label)}</span>
    </div>
  );
}

function RatingStars({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-0.5 text-accent">
      {Array.from({ length: 5 }).map((_, index) => (
        <Star key={index} size={15} fill={index < Math.round(rating) ? 'currentColor' : 'none'} />
      ))}
    </span>
  );
}

function MiniMap({ address }: { address: string | null }) {
  return (
    <div className="relative min-h-[118px] overflow-hidden rounded-lg border border-border bg-bg-primary">
      <div className="absolute inset-0 opacity-70 [background-image:linear-gradient(30deg,transparent_47%,rgb(var(--color-border-strong))_48%,rgb(var(--color-border-strong))_52%,transparent_53%),linear-gradient(150deg,transparent_47%,rgb(var(--color-bg-secondary))_48%,rgb(var(--color-bg-secondary))_52%,transparent_53%),linear-gradient(90deg,rgb(var(--color-border))_1px,transparent_1px)] [background-size:64px_64px,88px_88px,32px_32px]" />
      <div className="absolute left-1/2 top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-accent text-bg-primary shadow-cta">
        <MapPin size={24} fill="currentColor" />
      </div>
      <span className="sr-only">{address ?? 'Ubicacion por confirmar'}</span>
    </div>
  );
}

function QuickBookingCard({
  bookingHref,
  openStatus,
  profile,
}: {
  bookingHref: string;
  openStatus: { isOpen: boolean; label: string };
  profile: PublicBusinessProfile;
}) {
  const [serviceId, setServiceId] = useState('');
  const [professionalId, setProfessionalId] = useState('');
  const today = profile.schedules.find((day) => day.day === getZonedNow(profile.timezone)?.day);

  const selectedHref = buildBookingHref(profile.slug, {
    professionalId: professionalId || undefined,
    serviceId: serviceId || undefined,
  });

  return (
    <aside className="overflow-hidden rounded-card border border-border bg-bg-secondary/90 shadow-mockup">
      <div className="p-5 sm:p-6">
        <div className="mb-3 flex items-start justify-between gap-4">
          <div>
            <h2 className="font-heading text-xl font-semibold text-text-primary">Reserva rapida</h2>
            {/* <p className="mt-2 text-sm leading-6 text-text-secondary">Selecciona un servicio y agenda en segundos.</p> */}
          </div>
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border bg-accent/10 text-accent">
            <CalendarDays size={20} />
          </span>
        </div>

        <div className="grid gap-4">
          <label className="grid gap-2">
            <span className="text-sm font-semibold text-text-primary">Servicio</span>
            <span className="relative">
              <select
                value={serviceId}
                onChange={(event) => setServiceId(event.target.value)}
                className="h-12 w-full appearance-none rounded-button border border-border-strong bg-bg-primary px-4 pr-10 text-sm text-text-primary outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/20"
              >
                <option value="">Selecciona un servicio</option>
                {profile.services.map((service) => (
                  <option key={service.id} value={service.id}>{service.name}</option>
                ))}
              </select>
              <ChevronDown size={17} className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-text-secondary" />
            </span>
          </label>

          <label className="grid gap-2">
            <span className="text-sm font-semibold text-text-primary">Profesional</span>
            <span className="relative">
              <select
                value={professionalId}
                onChange={(event) => setProfessionalId(event.target.value)}
                className="h-12 w-full appearance-none rounded-button border border-border-strong bg-bg-primary px-4 pr-10 text-sm text-text-primary outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/20"
              >
                <option value="">Selecciona un profesional</option>
                {profile.professionals.map((professional) => (
                  <option key={professional.id} value={professional.id}>{professional.name}</option>
                ))}
              </select>
              <ChevronDown size={17} className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-text-secondary" />
            </span>
          </label>
{/* 
          <label className="grid gap-2">
            <span className="text-sm font-semibold text-text-primary">Fecha</span>
            <span className="relative">
              <input
                type="text"
                readOnly
                value=""
                placeholder="Selecciona una fecha"
                className="h-12 w-full rounded-button border border-border-strong bg-bg-primary px-4 pl-11 text-sm text-text-primary outline-none placeholder:text-text-secondary/70"
              />
              <CalendarDays size={17} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary" />
            </span>
          </label> */}

          <a
            href={selectedHref || bookingHref}
            className="mt-1 inline-flex min-h-12 items-center justify-center gap-2 rounded-button bg-accent px-5 py-3 font-heading font-semibold text-bg-primary shadow-cta transition-colors hover:bg-accent-hover"
          >
            Programar cita
          </a>
        </div>
      </div>
      <div className="border-t border-border p-5">
        <div className="flex items-start gap-3">
          <Clock3 size={19} className="mt-0.5 text-text-secondary" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-heading text-base font-semibold text-text-primary">Horario de hoy</h3>
              <p className="text-sm font-medium text-text-primary">{today ? formatScheduleRange(today.ranges) : openStatus.label}</p>
            </div>
              <span className={cn('rounded-full px-3 py-1 text-xs font-semibold float-right mt-1', openStatus.isOpen ? 'bg-success/10 text-success' : 'bg-bg-primary text-text-secondary')}>
                {openStatus.isOpen ? 'Abierto' : 'Cerrado'}
              </span>
          </div>
        </div>
      </div>

      <div className="border-t border-border p-5">
        <div className="flex items-start gap-3">
          <MapPin size={19} className="mt-0.5 text-text-secondary" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-heading text-base font-semibold text-text-primary">Direccion</h3>
              <p className="whitespace-pre-line text-sm leading-6 text-text-primary">{profile.address || 'Direccion por confirmar'}</p>
            </div>
            <div className="mt-1 float-right">
              <DirectionsLink address={profile.address} />
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}

export function BusinessProfileView({ className, profile, variant = 'public' }: BusinessProfileViewProps) {
  const isPreview = variant === 'preview';
  const primaryClassification = profile.primaryClassification ?? null;
  const additionalClassifications = profile.additionalClassifications ?? [];
  const bookingHref = buildBookingHref(profile.slug);
  const whatsappUrl = buildWhatsAppUrl(profile.phone, profile.name);
  const openStatus = useMemo(() => getOpenStatus(profile), [profile]);
  const rating = profile.ratingAverage;
  const hasRating = rating !== null && profile.ratingCount > 0;
  const visibleServices = isPreview ? profile.services.slice(0, 3) : profile.services.slice(0, 6);
  const visibleProfessionals = isPreview ? profile.professionals.slice(0, 2) : profile.professionals.slice(0, 4);
  const visiblePortfolio = isPreview ? profile.portfolioImages.slice(0, 3) : profile.portfolioImages.slice(0, 5);
  const visibleReviews = isPreview ? profile.reviews.slice(0, 2) : profile.reviews.slice(0, 4);
  const [activeSection, setActiveSection] = useState('servicios');

  const mobileSections = useMemo(() => [
    { id: 'servicios', label: 'Servicios', icon: Scissors },
    { id: 'equipo', label: 'Equipo', icon: UserRound },
    { id: 'fotos', label: 'Fotos', icon: ImageIcon },
    { id: 'info', label: 'Info', icon: Info },
  ], []);

  useEffect(() => {
    if (isPreview) return;

    const sections = mobileSections
      .map(({ id }) => document.getElementById(id))
      .filter((section): section is HTMLElement => Boolean(section));

    const updateActiveSection = () => {
      const activationLine = 190;
      const current = [...sections]
        .reverse()
        .find((section) => section.getBoundingClientRect().top <= activationLine);
      setActiveSection(current?.id ?? sections[0]?.id ?? 'servicios');
    };

    updateActiveSection();
    window.addEventListener('scroll', updateActiveSection, { passive: true });
    window.addEventListener('resize', updateActiveSection);
    return () => {
      window.removeEventListener('scroll', updateActiveSection);
      window.removeEventListener('resize', updateActiveSection);
    };
  }, [isPreview, mobileSections]);

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleServicesSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  };

  return (
    <article className={cn('bg-bg-primary text-text-primary', isPreview ? 'rounded-card border border-border' : 'min-h-screen', className)}>
      <section className="bg-bg-secondary/50 pb-0 lg:pb-10">
        <div className={cn('mx-auto grid max-w-7xl gap-0 pb-6 lg:gap-8 lg:px-6 lg:pb-10 lg:pt-8 lg:grid-cols-[1fr_0.95fr] lg:items-center', isPreview && 'gap-5 px-4 pb-8 pt-5')}>
          <div className="relative z-10 flex flex-col justify-center gap-5 px-5 pt-0 lg:px-0">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
              <div className={cn('flex shrink-0 items-center justify-center overflow-hidden rounded-full border-[3px] border-bg-primary bg-bg-primary shadow-mockup lg:border lg:border-border lg:bg-bg-primary/80 lg:shadow-none', isPreview ? 'h-24 w-24' : '-mt-12 h-24 w-24 lg:mt-0 lg:h-32 lg:w-32')}>
                {profile.imageUrl ? (
                  <img src={profile.imageUrl} alt={profile.name} className="h-full w-full object-contain" />
                ) : (
                  <ImageFallback label={profile.name} />
                )}
              </div>

              <div className="min-w-0 space-y-4">
                <div className="flex flex-wrap gap-2 pt-1 lg:pt-0">
                  <div className="inline-flex w-fit items-center gap-2 rounded-button border border-accent/30 bg-accent/10 px-3 py-1 text-xs font-semibold text-accent">
                    <Store size={14} />
                    Perfil público en OficioYa
                  </div>
                  <div
                    className={cn(
                      'inline-flex w-fit items-center gap-2 rounded-button border px-3 py-1 text-xs font-semibold',
                      openStatus.isOpen
                        ? 'border-success/40 bg-success/10 text-success'
                        : 'border-border-strong bg-bg-primary/60 text-text-secondary'
                    )}
                  >
                    <Clock3 size={14} />
                    {openStatus.label}
                  </div>
                </div>
                <div className="space-y-3">
                  <h1 className={cn('font-heading font-bold leading-tight text-text-primary', isPreview ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl')}>
                    {profile.name}
                  </h1>
                  {primaryClassification ? (
                    <p className="max-w-2xl break-words text-sm leading-5 text-text-secondary">
                      {primaryClassification.categoryName}
                      <span aria-hidden="true"> · </span>
                      {primaryClassification.typeName}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-text-secondary lg:mt-3 lg:text-white/92">
              {hasRating ? (
                <span className="inline-flex items-center gap-2">
                  <Star size={17} className="text-accent" fill="currentColor" />
                  <strong>{rating.toFixed(1)}</strong>
                  <span>{profile.ratingCount} resenas</span>
                </span>
              ) : (
                <span>Sin reseñas todavía</span>
              )}
              {profile.address ? (
                <span className="inline-flex items-center gap-2">
                  <MapPin size={17} />
                  {profile.address}
                </span>
              ) : null}
            </div>
            <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-row">
              {profile.supportsWhatsapp ? <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-button border border-border-strong bg-transparent px-3 py-3 text-sm font-heading font-semibold text-text-primary transition-colors hover:border-[#25D366] hover:text-[#25D366] sm:px-5 lg:border-transparent lg:bg-[#25D366] lg:text-bg-primary lg:hover:bg-[#34e879] lg:hover:text-bg-primary"
              >
                <MessageCircle size={18} />
                WhatsApp
              </a> : null}
              <a
                href={bookingHref}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-button bg-accent px-3 py-3 text-sm font-heading font-semibold text-bg-primary shadow-cta transition-colors hover:bg-accent-hover sm:px-5 lg:border lg:border-accent lg:bg-transparent lg:text-accent lg:shadow-none lg:hover:bg-accent lg:hover:text-bg-primary"
              >
                {/* <MonitorSmartphone size={18} /> */}
                <CalendarDays size={18} />
                Reservar cita
              </a>
            </div>
          </div>

          <div className={cn('relative order-first h-[220px] overflow-hidden bg-bg-primary shadow-[0_18px_50px_rgba(0,0,0,0.24)] sm:h-[260px] lg:order-none lg:h-[268px] lg:rounded-lg', isPreview && 'h-[140px] rounded-lg sm:h-[160px] lg:h-[170px]')}>
            <SocialButtons profile={profile} />
            {profile.bannerUrl ? (
              <>
                <img src={profile.bannerUrl} alt={`${profile.name} banner`} className="h-full w-full object-cover" />
                <div className="pointer-events-none absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-bg-secondary via-bg-secondary/65 to-transparent" />
              </>
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center text-text-secondary">
                <ImageIcon size={34} className="text-accent" />
                <p className="max-w-sm text-sm">Banner del negocio</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {!isPreview ? (
        <nav aria-label="Secciones del perfil" className="sticky top-20 z-30 grid grid-cols-4 border-y border-border bg-bg-primary/95 shadow-sm backdrop-blur-xl md:top-16 lg:hidden">
          {mobileSections.map(({ id, label, icon: Icon }) => {
            const isActive = activeSection === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => scrollToSection(id)}
                aria-current={isActive ? 'location' : undefined}
                className={cn(
                  'relative flex min-h-[68px] flex-col items-center justify-center gap-1 text-xs font-medium text-text-secondary transition-colors',
                  isActive && 'text-accent'
                )}
              >
                <Icon size={21} strokeWidth={1.8} />
                {label}
                <span className={cn('absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-accent transition-opacity', isActive ? 'opacity-100' : 'opacity-0')} />
              </button>
            );
          })}
        </nav>
      ) : null}

      <section className="relative z-10 bg-transparent lg:-mt-11">
        <div className="mx-auto grid max-w-[1320px] gap-6 px-3 py-3 sm:px-5 lg:grid-cols-[minmax(0,1fr)_360px] lg:px-0 lg:py-0 xl:grid-cols-[minmax(0,1fr)_420px]">
          <div className="min-w-0 space-y-4">
            <section id="servicios" className="scroll-mt-[148px] rounded-card border border-border bg-bg-secondary/85 p-4 shadow-mockup lg:scroll-mt-28 sm:p-5">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="font-heading text-xl font-semibold text-text-primary">Servicios</h2>
                <span className="text-sm font-semibold text-text-secondary">
                  {visibleServices.length} de {profile.services.length}
                </span>
              </div>

              {visibleServices.length > 0 ? (
                <form onSubmit={handleServicesSubmit} className="grid gap-2">
                  {visibleServices.map((service) => (
                    <div key={service.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-lg border border-border bg-bg-primary/70 p-3 sm:grid-cols-[minmax(0,1fr)_96px_120px_auto]">
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
                          <Scissors size={21} />
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate font-heading text-base font-semibold text-text-primary">{service.name}</span>
                          {service.description ? <span className="mt-1 block line-clamp-1 text-sm text-text-secondary">{service.description}</span> : null}
                        </span>
                      </div>
                      <span className="w-fit rounded-chip border border-border bg-bg-secondary px-3 py-1 text-xs font-semibold text-text-secondary sm:block">{formatDuration(service.durationMinutes)}</span>
                      <span className="font-heading text-base font-semibold text-accent sm:text-right sm:text-text-primary">{formatCurrency(service.priceCop)}</span>
                      <a
                        href={buildBookingHref(profile.slug, { serviceId: service.id })}
                        className="col-span-2 inline-flex min-h-10 items-center justify-center rounded-button border border-accent px-4 text-sm font-semibold text-accent transition-colors hover:bg-accent hover:text-bg-primary sm:col-span-1"
                      >
                        Elegir
                      </a>
                    </div>
                  ))}
                </form>
              ) : (
                <p className="rounded-lg border border-dashed border-border-strong px-4 py-8 text-sm text-text-secondary">No hay servicios publicos todavia.</p>
              )}
            </section>

            <div className="grid gap-4 xl:grid-cols-[minmax(0,0.92fr)_minmax(0,1fr)]">
              <section id="equipo" className="scroll-mt-[148px] rounded-card border border-border bg-bg-secondary/85 p-4 shadow-mockup lg:scroll-mt-28 sm:p-5">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <h2 className="font-heading text-xl font-semibold text-text-primary">Equipo</h2>
                  <span className="text-sm font-semibold text-text-secondary">
                    {visibleProfessionals.length} de {profile.professionals.length}
                  </span>
                </div>

                {visibleProfessionals.length > 0 ? (
                  <div className="-mx-1 flex snap-x gap-3 overflow-x-auto px-1 pb-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 sm:pb-0 xl:grid-cols-1">
                    {visibleProfessionals.map((professional) => (
                      <a
                        key={professional.id}
                        href={buildBookingHref(profile.slug, { professionalId: professional.id })}
                        className="flex w-[150px] shrink-0 snap-start flex-col items-center gap-3 rounded-lg border border-border bg-bg-primary/70 p-3 text-center transition-colors hover:border-accent sm:w-auto sm:flex-row sm:text-left"
                      >
                        <div className="h-20 w-20 shrink-0 overflow-hidden rounded-full bg-accent/10 sm:h-24 sm:rounded-lg">
                          {professional.imageUrl ? (
                            <img src={professional.imageUrl} alt={professional.name} className="h-full w-full object-cover" />
                          ) : (
                            <ImageFallback label={professional.name} />
                          )}
                        </div>
                        <div className="min-w-0">
                          <h3 className="truncate font-heading text-base font-semibold text-text-primary">{professional.name}</h3>
                          <p className="mt-1 text-sm text-text-secondary">Profesional</p>
                          <p className="mt-3 hidden items-start gap-2 text-xs leading-5 text-text-secondary sm:flex">
                            <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-success" />
                            Proxima disponibilidad segun agenda
                          </p>
                        </div>
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="rounded-lg border border-dashed border-border-strong px-4 py-8 text-sm text-text-secondary">No hay profesionales publicos todavia.</p>
                )}
              </section>

              <section id="fotos" className="scroll-mt-[148px] rounded-card border border-border bg-bg-secondary/85 p-4 shadow-mockup lg:scroll-mt-28 sm:p-5">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <h2 className="font-heading text-xl font-semibold text-text-primary">Portafolio</h2>
                  <span className="text-sm font-semibold text-text-secondary">Ver mas</span>
                </div>

                {visiblePortfolio.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2 overflow-hidden sm:h-[300px] sm:max-h-[300px] sm:grid-cols-2 sm:grid-rows-2 xl:h-[326px] xl:max-h-[326px]">
                    {visiblePortfolio.map((image, index) => (
                      <a
                        key={`${image}-${index}`}
                        href={image}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn('aspect-square min-h-0 overflow-hidden rounded-lg bg-bg-primary sm:aspect-auto', index === 0 && 'sm:row-span-2')}
                      >
                        <img src={image} alt={`Trabajo ${index + 1}`} className="h-full w-full object-cover transition-transform duration-300 hover:scale-[1.04]" />
                      </a>
                    ))}
                  </div>
                ) : (
                  <div className="flex min-h-[220px] items-center justify-center rounded-lg border border-dashed border-border-strong text-accent">
                    <ImageIcon size={30} />
                  </div>
                )}
              </section>
            </div>

            <section id="info" className="scroll-mt-[148px] grid gap-4 rounded-card border border-border bg-bg-secondary/85 p-4 shadow-mockup lg:grid-cols-2 lg:scroll-mt-28 sm:p-5">
              <div>
                <h2 className="font-heading text-xl font-semibold text-text-primary">Sobre el negocio</h2>
                <p className="mt-3 text-sm leading-6 text-text-secondary">
                  {profile.description || profile.headline || 'Informacion del negocio disponible en este perfil publico.'}
                </p>
                {additionalClassifications.length > 0 ? (
                  <div className="mt-5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-text-secondary">
                      Tambien ofrece
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {additionalClassifications.map((classification) => (
                        <span
                          key={classification.typeId ?? classification.typeName}
                          className="max-w-full break-words rounded-chip border border-border-strong px-2.5 py-1 text-xs text-text-secondary"
                        >
                          {classification.typeName}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
                <div className="mt-5 flex flex-wrap gap-4 text-xs font-medium text-text-secondary">
                  <span className="inline-flex items-center gap-2"><UserRound size={16} className="text-accent" />Atencion personalizada</span>
                  <span className="inline-flex items-center gap-2"><Award size={16} className="text-accent" />Productos premium</span>
                  <span className="inline-flex items-center gap-2"><Sparkles size={16} className="text-accent" />Ambiente exclusivo</span>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-text-primary">Ubicacion</h2>
                <div className="mt-3 grid gap-4 sm:grid-cols-[minmax(0,1fr)_220px] lg:grid-cols-1 xl:grid-cols-[minmax(0,1fr)_220px]">
                  <MiniMap address={profile.address} />
                  <div className="text-sm leading-6 text-text-secondary">
                    <p className="font-semibold text-text-primary">{profile.address || 'Direccion por confirmar'}</p>
                    <div className="mt-3">
                      <DirectionsLink address={profile.address} />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="rounded-card border border-border bg-bg-secondary/85 p-4 shadow-mockup sm:p-5">
              <div className="mb-5 flex items-center justify-between gap-3">
                <h2 className="font-heading text-xl font-semibold text-text-primary">Resenas</h2>
                <span className="text-sm font-semibold text-text-secondary">
                  {visibleReviews.length} de {profile.reviews.length}
                </span>
              </div>

              <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
                {hasRating ? (
                  <div className="flex items-center gap-5">
                    <div>
                      <p className="font-heading text-5xl font-semibold text-text-primary">{rating.toFixed(1)}</p>
                      <RatingStars rating={rating} />
                      <p className="mt-2 text-sm text-text-secondary">{profile.ratingCount} resenas</p>
                    </div>
                    <div className="flex-1 space-y-2">
                      {[5, 4, 3, 2, 1].map((stars) => {
                        const count = profile.reviews.filter((review) => review.rating === stars).length;
                        const percentage = profile.ratingCount > 0 ? (count / profile.ratingCount) * 100 : 0;

                        return (
                          <div key={stars} className="grid grid-cols-[20px_1fr_28px] items-center gap-2 text-xs text-text-secondary">
                            <span>{stars}</span>
                            <span className="h-2 overflow-hidden rounded-full bg-bg-primary">
                              <span className="block h-full rounded-full bg-accent" style={{ width: `${percentage}%` }} />
                            </span>
                            <span>{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <p className="rounded-lg border border-dashed border-border-strong px-4 py-8 text-sm text-text-secondary">
                    Este negocio aún no tiene reseñas públicas.
                  </p>
                )}

                {visibleReviews.length > 0 ? (
                  <div className="grid gap-3 md:grid-cols-2">
                    {visibleReviews.map((review) => (
                      <article key={review.id} className="rounded-card border border-border bg-bg-primary/70 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex min-w-0 items-center gap-3">
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent/10 font-heading text-sm font-semibold text-accent">
                              {getInitials(review.authorName)}
                            </span>
                            <div className="min-w-0">
                              <h3 className="truncate font-heading text-sm font-semibold text-text-primary">{review.authorName}</h3>
                              <p className="text-xs text-text-secondary">{formatReviewDate(review.createdAt)}</p>
                            </div>
                          </div>
                          <RatingStars rating={review.rating} />
                        </div>
                        <p className="mt-3 line-clamp-3 text-sm leading-6 text-text-secondary">{review.comment || 'Calificación sin comentario.'}</p>
                      </article>
                    ))}
                  </div>
                ) : (
                  <p className="rounded-lg border border-dashed border-border-strong px-4 py-8 text-sm text-text-secondary">Aun no hay resenas publicas.</p>
                )}
              </div>
            </section>
          </div>

          <div className="hidden lg:sticky lg:top-[5.0rem] lg:block lg:self-start">
            <QuickBookingCard bookingHref={bookingHref} openStatus={openStatus} profile={profile} />
          </div>
        </div>
      </section>
    </article>
  );
}
